import { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Send,
  X,
  ExternalLink,
  XCircle
} from 'lucide-react';

import { ROUTES } from '../../config/constants';
import api from '../../services/api';
import { marked } from 'marked';
import { useNavbar } from '../../context/NavbarContext';

const SESSION_CHAT_KEY = 'noraChatHistory';
const cn = (...classes) => classes.filter(Boolean).join(' ');
const TOOLTIP_SEEN_KEY = 'noraTooltipSeen_v2';

const createInitialMessage = (isOnCasesPageFlag) => ({
  id: `bot-initial-${Date.now()}`,
  text: isOnCasesPageFlag
    ? "Chat with Cases: Ask me anything about the cases listed here!"
    : "Hi there! 👋 I'm Nora, your NeurolawAI assistant. Click below or the icon to start!",
  sender: 'bot'
});

const Citation = ({ index, url }) => {
  const [showPopover, setShowPopover] = useState(false);

  let domain = 'Source link';
  try {
    const link = new URL(url);
    domain = link.hostname.replace('www.', '');
  } catch (error) {
    console.error("Invalid URL for citation:", url);
  }

  return (
    <div
      className="relative inline-block"
      onMouseEnter={() => setShowPopover(true)}
      onMouseLeave={() => setShowPopover(false)}
    >
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className="relative inline-flex items-center justify-center w-4 h-4 -top-1 mx-0.5 text-[10px] font-bold text-gray-700 bg-gray-100 border border-gray-200 rounded-full no-underline align-baseline transition-transform duration-150 ease-in-out hover:scale-125 hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-300"
        title={`Source: ${domain}`}
      >
        {index}
      </a>
      {showPopover && (
        <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-max max-w-xs px-3 py-2 bg-gray-900 text-white text-xs rounded-lg shadow-lg pointer-events-none animate-fade-in-fast z-10">
          <p className="font-medium">{domain}</p>
          <p className="opacity-70 text-[11px] mt-0.5">Click to open</p>
        </div>
      )}
    </div>
  );
};

const BotMessage = ({ text, sources = [] }) => {
  // Handle both old format (string) and new format (object with text and sources)
  const messageText = typeof text === 'string' ? text : text?.text || '';
  const sourcesArray = Array.isArray(sources) ? sources : (text?.sources || []);
  
  if (!messageText) {
    return <div className="text-sm text-gray-500">No response generated</div>;
  }

  // Updated regex to match grouped citations like [7, 8] or single [1]
  const citationBlockRegex = /(\[[\d,\s]+\])/g;
  
  // Split by citations
  const parts = messageText.split(citationBlockRegex);

  const renderMarkdown = (content) => {
    if (!content) return null;
    
    try {
      const html = marked.parse(content, { gfm: true, breaks: true });
      const unwrappedHtml = html.replace(/^<p>([\s\S]*)<\/p>\s*$/, '$1');
      return <div dangerouslySetInnerHTML={{ __html: unwrappedHtml }} />;
    } catch (error) {
      console.warn('Markdown parsing error:', error);
      return <div>{content}</div>;
    }
  };

  return (
    <div className="ai-response-content leading-relaxed">
      {parts.map((part, index) => {
        if (!part || part.trim() === '') return null;
        
        // Even indices are markdown content, odd indices are citations
        if (index % 2 === 1) {
          // Extract citation numbers from the bracket, e.g., "[7, 8]" -> ["7", "8"]
          const citationMatch = part.match(/\[([\d,\s]+)\]/);
          if (!citationMatch) return null;
          
          // Split by comma and clean up whitespace
          const citationNumbers = citationMatch[1]
            .split(',')
            .map(num => parseInt(num.trim(), 10))
            .filter(num => !isNaN(num));
          
          const citations = citationNumbers.map(citationIndex => {
            // Find source by 1-based index
            const source = sourcesArray.find(s => s?.index === citationIndex);
            
            if (source?.url) {
              return (
                <Citation 
                  key={`${index}-${citationIndex}`} 
                  index={citationIndex} 
                  url={source.url}
                />
              );
            }
            return null;
          }).filter(Boolean);
          
          return citations.length > 0 ? <span key={index} className="inline-flex items-center gap-0.5">{citations}</span> : null;
        }
        
        return (
          <div key={index}>
            {renderMarkdown(part)}
          </div>
        );
      })}
    </div>
  );
};

// --- Main ChatBot Widget Component ---
export default function NoraChat({ robotGifPath = "/robot.gif" }) {
  const location = useLocation();
  const isOnCasesPage = location.pathname.includes('/cases');
  const { sidebarExpanded } = useNavbar();

  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [chatId, setChatId] = useState(null);
  const [messages, setMessages] = useState(() => [createInitialMessage(isOnCasesPage)]);
  const [inputValue, setInputValue] = useState('');
  // const [pendingMessage, setPendingMessage] = useState(null); 
  const [isTyping, setIsTyping] = useState(false);
  const [hasSeenTooltip, setHasSeenTooltip] = useState(() => {
    try {
      return localStorage.getItem(TOOLTIP_SEEN_KEY) === 'true';
    } catch (e) {
      return false;
    }
  });
  const [showHoverTooltip, setShowHoverTooltip] = useState(false);
  const [chatWindowStyle, setChatWindowStyle] = useState({});
  const [navbarHeight, setNavbarHeight] = useState(0);

  const messagesEndRef = useRef(null);
  const chatWindowRef = useRef(null);
  const inputRef = useRef(null);
  const triggerRef = useRef(null);
  const generateBotResponseRef = useRef();
  const navigate = useNavigate();

  const iconRef = useRef(null);
  const [iconPosition, setIconPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [isClicking, setIsClicking] = useState(false);
  const dragOffset = useRef({ x: 0, y: 0 });
  const startDragPos = useRef({ x: 0, y: 0 });

  const CHAT_ICON_STORAGE_KEY = 'chatIconPosition';
  const DRAG_THRESHOLD = 5;

  const updateIconPosition = useCallback((newX, newY) => {
    if (!iconRef.current) return;

    const { innerWidth, innerHeight } = window;
    const { offsetWidth, offsetHeight } = iconRef.current;

    // Use smaller padding on mobile
    const isMobile = innerWidth <= 768;
    const isDesktop = innerWidth >= 1024;
    const padding = isMobile ? 8 : 16;

    // Add sidebar offset for desktop
    const sidebarOffset = isDesktop ? (sidebarExpanded ? 240 : 64) : 0;

    const maxX = innerWidth - offsetWidth - padding;
    const maxY = innerHeight - offsetHeight - padding;
    const minX = padding + sidebarOffset; // Add sidebar offset to minimum X
    const minY = navbarHeight + padding;

    const boundedX = Math.max(minX, Math.min(newX, maxX));
    const boundedY = Math.max(minY, Math.min(newY, maxY));

    setIconPosition({ x: boundedX, y: boundedY });
    localStorage.setItem(CHAT_ICON_STORAGE_KEY, JSON.stringify({ x: boundedX, y: boundedY }));
  }, [navbarHeight, sidebarExpanded]);

  useEffect(() => {
    const updateNavHeight = () => {
      const navElement = document.querySelector('header');
      if (navElement) {
        setNavbarHeight(navElement.offsetHeight);
      }
    };

    updateNavHeight();
    window.addEventListener('resize', updateNavHeight);

    return () => window.removeEventListener('resize', updateNavHeight);
  }, []);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, []);

  const dismissTooltip = useCallback(() => {
    setHasSeenTooltip(true);
    try {
      localStorage.setItem(TOOLTIP_SEEN_KEY, 'true');
    } catch (e) {}
  }, []);

  const handleOpenChat = useCallback(() => {
    setIsOpen(true);
    setIsMinimized(false);
    if (!hasSeenTooltip) {
      dismissTooltip();
    }
  }, [hasSeenTooltip, dismissTooltip]);

  const handleCloseChat = useCallback(() => {
    setIsOpen(false);
    setIsMinimized(false);
    // Don't clear chatId or messages - keep session alive until page refresh
  }, []);

  // Update icon position when sidebar expands/collapses
  useEffect(() => {
    if (iconPosition.x > 0 || iconPosition.y > 0) {
      updateIconPosition(iconPosition.x, iconPosition.y);
    }
  }, [sidebarExpanded, updateIconPosition]);

  useEffect(() => {
    const savedPosition = localStorage.getItem(CHAT_ICON_STORAGE_KEY);
    if (savedPosition) {
      const { x, y } = JSON.parse(savedPosition);
      setTimeout(() => {
        updateIconPosition(x, y);
      }, 0);
    } else {
      setTimeout(() => {
        if (iconRef.current) {
          const { innerWidth, innerHeight } = window;
          const { offsetWidth, offsetHeight } = iconRef.current;
          updateIconPosition(innerWidth - offsetWidth - 16, innerHeight - offsetHeight - 16);
        }
      }, 0);
    }

    const handleMouseMove = (e) => {
      if (isClicking) {
        const dx = Math.abs(e.clientX - startDragPos.current.x);
        const dy = Math.abs(e.clientY - startDragPos.current.y);
        if (dx > DRAG_THRESHOLD || dy > DRAG_THRESHOLD) {
          setIsDragging(true);
          setIsClicking(false);
        }
      }

      if (isDragging) {
        updateIconPosition(
          e.clientX - dragOffset.current.x,
          e.clientY - dragOffset.current.y
        );
      }
    };

    const handleMouseUp = () => {
      if (isClicking && !isDragging) {
        handleOpenChat();
      }

      if (isDragging && iconRef.current) {
        const { innerWidth, innerHeight } = window;
        const { offsetWidth, offsetHeight } = iconRef.current;
        const { x, y } = iconPosition;

        const distToLeft = x + offsetWidth / 2;
        const distToRight = innerWidth - (x + offsetWidth / 2);
        const distToBottom = innerHeight - (y + offsetHeight / 2);

        const minDist = Math.min(distToLeft, distToRight, distToBottom);

        let newX = x;
        let newY = y;

        if (minDist === distToLeft) {
          newX = 16;
        } else if (minDist === distToRight) {
          newX = innerWidth - offsetWidth - 16;
        } else {
          newY = innerHeight - offsetHeight - 16;
        }

        updateIconPosition(newX, newY);
      }

      setIsDragging(false);
      setIsClicking(false);
    };

    const handleTouchMove = (e) => {
      const touch = e.touches[0];

      if (isClicking) {
        const dx = Math.abs(touch.clientX - startDragPos.current.x);
        const dy = Math.abs(touch.clientY - startDragPos.current.y);
        if (dx > DRAG_THRESHOLD || dy > DRAG_THRESHOLD) {
          setIsDragging(true);
          setIsClicking(false);
        }
      }

      if (isDragging) {
        e.preventDefault();
        updateIconPosition(
          touch.clientX - dragOffset.current.x,
          touch.clientY - dragOffset.current.y
        );
      }
    };

    const handleTouchEnd = () => {
      if (isClicking && !isDragging) {
        handleOpenChat();
      }

      if (isDragging && iconRef.current) {
        const { innerWidth, innerHeight } = window;
        const { offsetWidth, offsetHeight } = iconRef.current;
        const { x, y } = iconPosition;

        const distToLeft = x + offsetWidth / 2;
        const distToRight = innerWidth - (x + offsetWidth / 2);
        const distToBottom = innerHeight - (y + offsetHeight / 2);

        const minDist = Math.min(distToLeft, distToRight, distToBottom);

        let newX = x;
        let newY = y;

        if (minDist === distToLeft) {
          newX = 16;
        } else if (minDist === distToRight) {
          newX = innerWidth - offsetWidth - 16;
        } else {
          newY = innerHeight - offsetHeight - 16;
        }

        updateIconPosition(newX, newY);
      }

      setIsDragging(false);
      setIsClicking(false);
    };

    const handleResize = () => {
      if (iconRef.current) {
        updateIconPosition(iconPosition.x, iconPosition.y);
      }
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    window.addEventListener('resize', handleResize);
    window.addEventListener('touchmove', handleTouchMove, { passive: false });
    window.addEventListener('touchend', handleTouchEnd);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('touchmove', handleTouchMove);
      window.removeEventListener('touchend', handleTouchEnd);
    };
  }, [isDragging, isClicking, updateIconPosition, iconPosition.x, iconPosition.y, handleOpenChat]);

  const handleMouseDown = (e) => {
    // Only handle mouse down on the icon itself, not its children
    if (e.target.closest('.no-drag') || !e.target.closest('[data-icon-clickable]')) {
      return;
    }

    e.preventDefault();
    e.stopPropagation();
    setIsClicking(true);
    startDragPos.current = { x: e.clientX, y: e.clientY };
    dragOffset.current = {
      x: e.clientX - iconPosition.x,
      y: e.clientY - iconPosition.y,
    };
  };

  const handleTouchStart = (e) => {
    if (e.target.closest('.no-drag')) return;

    const touch = e.touches[0];
    setIsClicking(true);
    startDragPos.current = { x: touch.clientX, y: touch.clientY };
    dragOffset.current = {
      x: touch.clientX - iconPosition.x,
      y: touch.clientY - iconPosition.y,
    };
  };

  useEffect(() => {
    if (isOpen && !isMinimized) {
      scrollToBottom();
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [messages, isOpen, isMinimized, scrollToBottom]);

  useEffect(() => {
    function handleClickOutside(event) {
      // Don't close if clicking on the draggable icon or if currently dragging
      if (isDragging || isClicking) return;

      if (
        (!isOpen || isMinimized) && !isMinimized &&
        chatWindowRef.current && !chatWindowRef.current.contains(event.target) &&
        triggerRef.current && !triggerRef.current.contains(event.target)
      ) {
        setIsMinimized(true);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen, isMinimized, isDragging, isClicking]);

  useEffect(() => {
    if (!isOpen || isMinimized) {
      setChatWindowStyle({});
      return;
    }

    const { innerWidth, innerHeight } = window;
    const { x: iconX, y: iconY } = iconPosition;

    const chatWindowWidth = 440;
    const chatWindowHeight = Math.min(Math.max(500, innerHeight * 0.8), 650);
    const iconWidth = 80;
    const iconHeight = 80;
    const padding = 16;

    let finalStyle = {};

    if ((innerWidth - (iconX + iconWidth)) > iconX) {
      finalStyle.left = `${iconX + iconWidth + padding}px`;
    } else {
      finalStyle.left = `${iconX - chatWindowWidth - padding}px`;
    }

    let idealTop = iconY;

    if (idealTop + chatWindowHeight + padding > innerHeight) {
      idealTop = iconY + iconHeight - chatWindowHeight;
    }

    finalStyle.top = `${Math.max(
      navbarHeight + padding,
      Math.min(idealTop, innerHeight - chatWindowHeight - padding)
    )}px`;

    let finalLeft = parseFloat(finalStyle.left);
    finalStyle.left = `${Math.max(
      padding,
      Math.min(finalLeft, innerWidth - chatWindowWidth - padding)
    )}px`;

    setChatWindowStyle(finalStyle);
  }, [isOpen, isMinimized, iconPosition, navbarHeight]);

  const generateBotResponse = useCallback(async (userInput, currentChatId, chatHistory, onResponse, onError, onLoading) => {
    generateBotResponseRef.current = generateBotResponse;
    if (!userInput || typeof userInput !== 'string') {
      if (onError) onError({ id: `asst-nora-error-input-${Date.now()}`, sender: 'bot', text: "Invalid input. Please type a message." });
      if (onLoading) onLoading(false);
      return;
    }
    if (!currentChatId) {
      if (onError) onError({ id: `asst-nora-error-chatid-${Date.now()}`, sender: 'bot', text: "Chat session not initialized." });
      if (onLoading) onLoading(false);
      return;
    }
    if (onLoading) onLoading(true);
    try {
      // Use Nora API endpoint
      const response = await api.post(`/api/v1/nora/${currentChatId}/ask`, {
        query: userInput,
        sessionId: currentChatId,
        history: chatHistory
      });
      
      const responseData = response?.data?.data || response?.data;
      const botText = responseData?.answer || responseData?.content || "Nora AI returned an empty response.";
      const sources = responseData?.sources || [];
      const action = responseData?.action || null;
      
      if (onResponse) onResponse({ 
        id: `asst-nora-${Date.now()}`, 
        sender: 'bot', 
        text: botText,
        metadata: {
          sources: sources,
          action: action
        }
      });
    } catch (err) {
      const fallback = err.response?.data?.message || err.message || "Nora AI request failed.";
      if (onError) onError({ id: `asst-nora-error-${Date.now()}`, sender: 'bot', text: fallback });
    } finally {
      if (onLoading) onLoading(false);
    }
  }, []);

  const handleSendMessage = useCallback(async () => {
    const textInput = inputValue.trim();
    if (!textInput) return;

    const userMessage = { id: `${Date.now()}-user`, text: textInput, sender: 'user' };
    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setIsTyping(true);

    let currentChatId = chatId;

    try {
      // Create session ID if needed (session is created on backend when first message is sent)
      if (currentChatId === null) {
        currentChatId = `chat-${Date.now()}`;
        setChatId(currentChatId);
      }

      const conversationMessages = messages.slice(1);
      const chatHistoryForAPI = conversationMessages.slice(-4).map(msg => ({ role: msg.sender === 'user' ? 'user' : 'assistant', content: msg.text }));

      await generateBotResponse(
        textInput,
        currentChatId,
        chatHistoryForAPI,
        (botMessage) => setMessages(prev => [...prev, botMessage]),
        (errorMsg) => setMessages(prev => [...prev, errorMsg]),
        (loading) => setIsTyping(loading)
      );

    } catch (error) {
      setMessages(prev => [...prev, { id: `bot-error-${Date.now()}`, sender: 'bot', text: error.message || "An error occurred." }]);
      setIsTyping(false);
    }
  }, [inputValue, messages, chatId, generateBotResponse]);

  const handleKeyPress = useCallback((e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  }, [handleSendMessage]);

  return (
    <div className="relative">
      <div
        ref={triggerRef}
        className="relative flex justify-end items-end"
        style={{
          position: 'fixed',
          left: iconPosition.x,
          top: iconPosition.y,
          cursor: isDragging ? 'grabbing' : 'grab',
          zIndex: 100,
          pointerEvents: 'auto'
        }}
        onMouseDown={handleMouseDown}
        onTouchStart={handleTouchStart}
      >

        {/* Welcome Tooltip */}
        {!hasSeenTooltip && !isOpen && (
          <div 
            className="absolute bottom-[calc(100%+10px)] right-0 w-48 sm:w-64 bg-white rounded-xl shadow-lg border border-gray-300 p-3 sm:p-4 animate-fade-in pointer-events-auto"
            style={{ pointerEvents: 'auto' }}
          >
            <button
              onClick={(e) => { e.stopPropagation(); dismissTooltip(); }}
              className="absolute top-2 right-2 text-gray-400 hover:text-gray-600 p-1 rounded-full hover:bg-gray-100 transition-colors z-10 no-drag"
              aria-label="Dismiss"
            >
              <XCircle size={16} />
            </button>
            <div className="flex items-center gap-2 mb-2">
              <img src={robotGifPath} alt="" className="w-7 h-7" />
              <h4 className="font-medium text-gray-900 text-sm">Meet Nora!</h4>
            </div>
            <p className="text-xs text-gray-500 mb-3">Your AI legal assistant. Click to start chatting!</p>
            <button
              onClick={(e) => { e.stopPropagation(); handleOpenChat(); }}
              className="w-full bg-gray-900 hover:bg-gray-800 text-white rounded-lg py-2 px-4 text-xs font-medium transition-colors shadow-sm no-drag"
            >
              Start Chatting
            </button>
          </div>
        )}

        {/* Floating Icon */}
        {!isOpen && (
          <div
            ref={iconRef}
            onMouseEnter={() => setShowHoverTooltip(true)}
            onMouseLeave={() => setShowHoverTooltip(false)}
            onTouchStart={handleTouchStart}
            className="bg-gray-50 w-12 h-12 sm:w-16 sm:h-16 md:w-20 md:h-20 cursor-pointer relative pointer-events-auto transition-transform duration-200 hover:scale-110 focus:outline-none focus:ring-4 focus:ring-gray-300 focus:ring-offset-2 rounded-full"
            title="Chat with Nora AI"
            aria-label="Open Nora AI Chatbot"
            role="button"
            tabIndex={0}
            onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && !isDragging && handleOpenChat()}
            style={{ touchAction: 'none' }}
            data-icon-clickable="true"
          >
            <img
              src={robotGifPath}
              alt="Nora AI Assistant"
              className="w-full h-full object-contain"
              data-icon-clickable="true"
            />
            {showHoverTooltip && hasSeenTooltip && (
              <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-1.5 bg-gray-900 text-white text-xs rounded-lg shadow-lg whitespace-nowrap pointer-events-none animate-fade-in-fast">
                Chat with Nora
              </div>
            )}
          </div>
        )}
      </div>

      {/* Chat Window */}
      {isOpen && !isMinimized && (
        <div
          ref={chatWindowRef}
          className="fixed w-80 sm:w-96 md:w-[440px] bg-white rounded-xl shadow-lg flex flex-col overflow-hidden border border-gray-300 animate-slide-up-fade pointer-events-auto"
          style={{ height: "clamp(400px, 85vh, 600px)", ...chatWindowStyle, zIndex: 100 }}
        >
          {/* Header */}
          <div className="bg-gray-900 p-3 sm:p-4 flex items-center justify-between flex-shrink-0">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center overflow-hidden shadow-sm flex-shrink-0">
                <img src={robotGifPath} alt="Nora" className="w-full h-full object-cover" />
              </div>
              <div>
                <h3 className="text-white font-medium text-sm sm:text-base leading-tight">
                  {isOnCasesPage ? "Chat with Cases" : "Nora AI"}
                </h3>
                <div className="flex items-center mt-0.5">
                  <div className="w-1.5 h-1.5 bg-green-400 rounded-full mr-1.5 animate-pulse"></div>
                  <span className="text-xs text-gray-400">Online</span>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-1">

              <button 
                onClick={handleCloseChat} 
                className="text-gray-400 hover:text-white transition-colors rounded-lg p-2 hover:bg-gray-800 focus:outline-none" 
                aria-label="Close chat"
              >
                <X size={18} />
              </button>
            </div>
          </div>

          {/* Messages */}
          <div className="flex-1 p-4 overflow-y-auto bg-gray-50 space-y-3 scrollbar-thin">
            {messages.map((message) => (
              <div key={message.id} className={cn('flex', message.sender === 'user' ? 'justify-end' : 'justify-start')}>
                <div className={cn(
                  "rounded-xl py-2.5 px-3.5 max-w-[85%] shadow-sm break-words text-sm leading-relaxed",
                  message.sender === 'user' 
                    ? 'bg-gray-900 text-white rounded-br-none' 
                    : 'bg-white text-gray-800 border border-gray-200 rounded-bl-none'
                )}>
                  {message.sender === 'bot' ? (
                    <BotMessage 
                      text={message.text} 
                      sources={message.metadata?.sources || message.sources || []} 
                    />
                  ) : (
                    message.text.split('\n').map((line, i) => <span key={i} className="block">{line}</span>)
                  )}
                </div>
              </div>
            ))}
            {isTyping && (
              <div className="flex justify-start">
                <div className="bg-white border border-gray-200 rounded-xl py-2 px-4 rounded-bl-none shadow">
                  <div className="flex space-x-1 items-center h-5">
                    <div className="bg-gray-400 rounded-full w-1.5 h-1.5 animate-bounce"></div>
                    <div className="bg-gray-400 rounded-full w-1.5 h-1.5 animate-bounce animation-delay-150"></div>
                    <div className="bg-gray-400 rounded-full w-1.5 h-1.5 animate-bounce animation-delay-300"></div>
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Chat input */}
          <div className="border-t border-gray-200 p-3 flex items-center gap-2 bg-gray-50 flex-shrink-0">
            <input ref={inputRef} type="text" value={inputValue} onChange={(e) => setInputValue(e.target.value)} onKeyPress={handleKeyPress} placeholder="Ask Nora..." className="flex-1 border border-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-gray-400 focus:border-transparent resize-none text-sm shadow-sm bg-white" aria-label="Chat input" />
            <button onClick={handleSendMessage} disabled={!inputValue.trim()} className={cn("bg-gray-800 hover:bg-gray-900 text-white rounded-lg p-2 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-gray-400 focus:ring-offset-1 shadow-sm", !inputValue.trim() ? 'opacity-50 cursor-not-allowed' : 'opacity-100')} aria-label="Send message">
              <Send size={18} />
            </button>
          </div>
        </div>
      )}

      {/* CSS Keyframes */}
      <style jsx global>{`
        @keyframes slide-up-fade { from { opacity: 0; transform: translateY(15px) scale(0.98); } to { opacity: 1; transform: translateY(0) scale(1); } }
        .animate-slide-up-fade { animation: slide-up-fade 0.3s ease-out forwards; }
        @keyframes fade-in { from { opacity: 0; } to { opacity: 1; } }
        .animate-fade-in { animation: fade-in 0.5s ease-out forwards; }
        .animate-fade-in-fast { animation: fade-in 0.2s ease-out forwards; }
        @keyframes bounce { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(-4px); } }
        .animate-bounce { animation: bounce 1s infinite ease-in-out; }
        .animation-delay-150 { animation-delay: 0.15s; }
        .animation-delay-300 { animation-delay: 0.3s; }
        .scrollbar-thin { scrollbar-width: thin; scrollbar-color: #cbd5e1 #f1f5f9; }
        .scrollbar-thin::-webkit-scrollbar { width: 6px; }
        .scrollbar-thin::-webkit-scrollbar-track { background: #f1f5f9; border-radius: 3px; }
        .scrollbar-thin::-webkit-scrollbar-thumb { background-color: #cbd5e1; border-radius: 3px; border: 1px solid #f1f5f9; }
        .hover\\:scrollbar-thumb-gray-400:hover::-webkit-scrollbar-thumb { background-color: #94a3b8; }
        @media (max-width: 768px) {
  .pointer-events-auto {
    touch-action: none;
  }
}
  @media (max-width: 768px) {
  .fixed { position: fixed !important; }
  .animate-slide-up-fade { animation-duration: 0.2s; }
}
@media (max-width: 480px) {
  .rounded-xl { border-radius: 0.75rem; }
  .shadow-2xl { box-shadow: 0 10px 25px -3px rgb(0 0 0 / 0.1), 0 4px 6px -2px rgb(0 0 0 / 0.05); }
}
      `}</style>
    </div>
  );
}